import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LineChartIcon from "@rsuite/icons/LineChart";

type TrendItem = {
  date: string;
  count?: number;
  total?: number;
};

const formatNumber = (value?: number) => (value ?? 0).toLocaleString("fa-IR");

const toJalali = (date: string) => {
  const utcDate = new Date(date);
  return utcDate.toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

const toJalaliShort = (date: string) => {
  const utcDate = new Date(date);
  return utcDate.toLocaleDateString("fa-IR", {
    month: "numeric",
    day: "numeric",
  });
};

export default function ChartITem({ newData }: { newData?: TrendItem[] | null }) {
  const rows = Array.isArray(newData) ? newData : [];

  const chartData = useMemo(
    () =>
      [...rows]
        .slice()
        .reverse()
        .map((item) => ({
          ...item,
          label: toJalaliShort(item.date),
          total: item.total ?? 0,
          count: item.count ?? 0,
        })),
    [rows]
  );

  const monthTotal = rows.reduce((sum, item) => sum + (item.total || 0), 0);
  const monthCount = rows.reduce((sum, item) => sum + (item.count || 0), 0);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
            <LineChartIcon className="text-[16px]" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">روند فروش ماهانه</h2>
            <p className="text-xs text-slate-500">فروش روزبه‌روز یک ماه اخیر</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
          <span>
            {rows.length} روز · {formatNumber(monthCount)} سفارش
          </span>
          <span className="font-semibold text-slate-800">
            {formatNumber(monthTotal)} تومان
          </span>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className="h-56 w-full px-2 pt-4 sm:h-64 sm:px-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0f172a" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#0f172a" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={48}
                tickFormatter={(value) => Number(value).toLocaleString("fa-IR")}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [
                  formatNumber(value),
                  name === "total" ? "مبلغ (تومان)" : "تعداد",
                ]}
                labelFormatter={(label) => `تاریخ: ${label}`}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#0f172a"
                strokeWidth={2}
                fill="url(#salesFill)"
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="px-4 py-12 text-center text-sm text-slate-500">
          داده‌ای برای نمایش وجود ندارد
        </div>
      )}

      {rows.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="sticky top-0 grid grid-cols-3 gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-semibold text-slate-600 sm:px-5">
            <div className="text-start">تاریخ</div>
            <div className="text-start">مبلغ</div>
            <div className="text-start">تعداد خرید</div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {rows.map((item, index) => (
              <div
                key={`${item.date}-${index}`}
                className="grid grid-cols-3 gap-3 border-b border-slate-50 px-4 py-3 text-sm transition hover:bg-slate-50 sm:px-5"
              >
                <div className="text-start text-slate-600" dir="ltr">
                  {toJalali(item.date)}
                </div>
                <div className="text-start font-semibold text-slate-800">
                  {formatNumber(item.total)}
                </div>
                <div className="text-start text-slate-600">{formatNumber(item.count)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
